import React, { useState, useEffect } from 'react';
import { Sparkles, X, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';

const FloatingDiscoveryBox = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInsight, setSelectedInsight] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [canOpen, setCanOpen] = useState(true);
  const { toast } = useToast();

  const insights = [
    {
      title: "Aynı trafikle daha fazla müşteri kazanabilir misiniz?",
      description: "Sitenizi AI ile analiz edip en hızlı gelir artışı sağlayacak 3 iyileştirmeyi paylaşıyoruz.",
      cta: "Ücretsiz mini analiz iste"
    }
  ];

  const [formData, setFormData] = useState({
    website: '',
    contact: ''
  });

  useEffect(() => {
    // Check if box was opened today
    const lastOpened = localStorage.getItem('ruyan_discovery_opened');
    const today = new Date().toDateString();
    
    if (lastOpened === today) {
      setCanOpen(false);
    }
  }, []);

  const handleBoxClick = () => {
    if (!canOpen) return;

    // Select random insight
    const randomInsight = insights[Math.floor(Math.random() * insights.length)];
    setSelectedInsight(randomInsight);
    setIsModalOpen(true);

    // Mark as opened today
    const today = new Date().toDateString();
    localStorage.setItem('ruyan_discovery_opened', today);
    setCanOpen(false);
  };

  const handleCTAClick = () => {
    setIsFormOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Mock submission
    console.log('Form submitted:', formData);
    
    toast({
      title: "Harika! ✨",
      description: "48 saat içinde 3 maddelik fırsat listesini ileteceğiz.",
    });

    setIsModalOpen(false);
    setIsFormOpen(false);
    setFormData({ website: '', contact: '' });
  };

  return (
    <>
      {/* Floating Discovery Box */}
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleBoxClick}
        className={`fixed bottom-24 right-8 z-50 transition-all duration-500 ${
          canOpen ? 'cursor-pointer' : 'cursor-default opacity-50'
        }`}
        style={{
          animation: canOpen ? 'floating-breathe 4s ease-in-out infinite' : 'none'
        }}
      >
        {/* Tooltip */}
        {isHovered && canOpen && (
          <div className="absolute bottom-full mb-3 right-0 px-4 py-2 bg-slate-900/90 backdrop-blur-xl border border-cyan-500/30 rounded-lg text-sm text-white whitespace-nowrap animate-fade-in">
            Bir fikir keşfet
            <div className="absolute top-full right-4 w-0 h-0 border-l-8 border-r-8 border-t-8 border-transparent border-t-slate-900/90"></div>
          </div>
        )}

        {/* Box */}
        <div className={`relative w-16 h-16 bg-slate-800/40 backdrop-blur-xl border rounded-2xl transition-all duration-500 ${
          canOpen 
            ? 'border-cyan-500/30 hover:border-cyan-500/60 hover:shadow-2xl hover:shadow-cyan-500/30 hover:scale-110' 
            : 'border-slate-700/30'
        }`}>
          {/* Icon */}
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className={`w-7 h-7 transition-all duration-500 ${
              canOpen ? 'text-cyan-400' : 'text-gray-600'
            }`} />
          </div>

          {/* Glow ring */}
          {canOpen && (
            <div className="absolute inset-0 rounded-2xl border-2 border-cyan-500/20 animate-ping-slow"></div>
          )}

          {/* Status indicator */}
          {!canOpen && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-slate-900 border-2 border-slate-700 rounded-full flex items-center justify-center">
              <span className="text-[10px] text-gray-500">✓</span>
            </div>
          )}
        </div>

        {/* Next available text */}
        {!canOpen && (
          <div className="absolute top-full mt-2 right-0 text-[10px] text-gray-500 whitespace-nowrap">
            Yarın yeni keşif
          </div>
        )}
      </div>

      {/* Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4 animate-fade-in">
          <div 
            className="relative max-w-lg w-full bg-slate-900/95 backdrop-blur-xl border border-cyan-500/30 rounded-3xl p-8 shadow-2xl animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => {
                setIsModalOpen(false);
                setIsFormOpen(false);
              }}
              className="absolute top-4 right-4 w-10 h-10 rounded-full bg-slate-800/50 border border-slate-700/50 flex items-center justify-center text-gray-400 hover:text-white hover:border-cyan-500/50 transition-all duration-300"
            >
              <X className="w-5 h-5" />
            </button>

            {!isFormOpen ? (
              <>
                {/* Badge */}
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-full mb-6">
                  <Sparkles className="w-4 h-4 text-cyan-400" />
                  <span className="text-cyan-400 text-sm font-medium">Bugünün Keşfi</span>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold text-white mb-4">
                  {selectedInsight?.title}
                </h3>

                <p className="text-gray-400 text-base mb-8 leading-relaxed">
                  {selectedInsight?.description}
                </p>

                {/* CTA Button */}
                <Button
                  onClick={handleCTAClick}
                  className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-cyan-500/50 group"
                >
                  {selectedInsight?.cta}
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>

                {/* Footer note */}
                <p className="text-xs text-gray-500 text-center mt-6">
                  48 saat içinde e-posta ile
                </p>
              </>
            ) : (
              <>
                {/* Form */}
                <h3 className="text-2xl font-bold text-white mb-6">
                  15 Dakikalık Mini Analiz
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <Label htmlFor="website" className="text-gray-300 mb-2 block">
                      Web Siteniz
                    </Label>
                    <Input
                      id="website"
                      type="url"
                      placeholder="https://ornek.com"
                      value={formData.website}
                      onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                      required
                      className="bg-slate-900/50 border-cyan-500/30 text-white focus:border-cyan-500 focus:ring-cyan-500 h-12 rounded-xl"
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact" className="text-gray-300 mb-2 block">
                      E-posta veya WhatsApp
                    </Label>
                    <Input
                      id="contact"
                      type="text"
                      placeholder="ornek@mail.com veya +90 XXX XXX XX XX"
                      value={formData.contact}
                      onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                      required
                      className="bg-slate-900/50 border-cyan-500/30 text-white focus:border-cyan-500 focus:ring-cyan-500 h-12 rounded-xl"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl transition-all duration-300 hover:scale-105"
                  >
                    Mini Analiz İste
                  </Button>

                  <p className="text-xs text-gray-500 text-center">
                    Ücretsiz • Bağlayıcı değil • 48 saat içinde yanıt
                  </p>
                </form>
              </>
            )}

            {/* Decorative glow */}
            <div className="absolute -top-20 -right-20 w-40 h-40 bg-cyan-500/20 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-20 -left-20 w-40 h-40 bg-teal-500/20 rounded-full blur-3xl pointer-events-none"></div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes floating-breathe {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-8px);
          }
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.05);
            opacity: 0.4;
          }
          100% {
            transform: scale(1.1);
            opacity: 0;
          }
        }

        .animate-ping-slow {
          animation: ping-slow 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        @keyframes scale-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        .animate-scale-in {
          animation: scale-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
        }
      `}</style>
    </>
  );
};

export default FloatingDiscoveryBox;
